// src/components/WithdrawalModal.js
import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { DollarSign, CreditCard, User, Building } from "lucide-react";

// -------- helpers --------
function joinUrl(base, path) {
  return `${String(base).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
}

function getAccessToken() {
  return localStorage.getItem("access_token") || localStorage.getItem("token") || "";
}

function resolveApiBase() {
  const stored =
    localStorage.getItem("api_base_used") ||
    localStorage.getItem("api_base") ||
    "";

  if (stored) return stored.replace(/\/+$/, "");

  const origin = window.location.origin;
  const isDev = /:3000$/.test(origin);

  if (isDev) return "https://www.cofy.app/api/trader/index.php";

  return `${origin}/api/trader`;
}

export default function WithdrawalModal({ open, onClose, onSuccess, balance }) {
  const [amount, setAmount] = useState("");
  const [pixKey, setPixKey] = useState("");
  const [bankName, setBankName] = useState("");
  const [cpf, setCpf] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const API_BASE = useMemo(() => resolveApiBase(), []);
  const authHeaders = useMemo(() => {
    const t = getAccessToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const fetchUserProfile = useCallback(async () => {
    setLoadingProfile(true);
    try {
      const response = await axios.get(joinUrl(API_BASE, "/user/profile"), {
        headers: authHeaders,
      });

      setPixKey(response.data.pix_key || response.data.pixKey || "");
      setBankName(response.data.bank_name || response.data.bankName || "");
      setCpf(response.data.cpf || "");
    } catch (error) {
      console.error("Error fetching profile:", error?.response?.data || error);
    } finally {
      setLoadingProfile(false);
    }
  }, [API_BASE, authHeaders]);

  useEffect(() => {
    if (open) {
      fetchUserProfile();
    }
  }, [open, fetchUserProfile]);

  const handleWithdrawal = async () => {
    const value = parseFloat(amount);

    if (!amount || !Number.isFinite(value) || value <= 0) {
      toast.error("Digite um valor válido");
      return;
    }

    if (value > (Number(balance) || 0) + 0.00001) {
      toast.error("Saldo insuficiente");
      return;
    }

    if (!pixKey || !cpf) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    setLoading(true);
    try {
      // 1) Salvar dados bancários no perfil (best effort)
      try {
        await axios.put(
          joinUrl(API_BASE, "/user/bank-info"),
          { pix_key: pixKey, bank_name: bankName, cpf },
          { headers: { ...authHeaders } }
        );
      } catch (err) {
        const st = err?.response?.status;
        console.warn("bank-info falhou, seguindo com saque:", st, err?.response?.data || err);
        // ignora 404 e segue; outros erros também não travam aqui
      }

      // 2) Solicitação de saque - tenta endpoints compatíveis
      const payload = { amount: value, pix_key: pixKey, bank_name: bankName, cpf };

      const candidates = [
        "/withdrawal/create",
        "/withdraw/create",
        "/withdrawal",
        "/withdraw",
        "/payments/withdraw",
        "/payments/withdrawal",
      ];

      let okResp = null;
      let lastErr = null;

      for (const p of candidates) {
        try {
          okResp = await axios.post(joinUrl(API_BASE, p), payload, {
            headers: { ...authHeaders },
          });
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          // segue tentando outros
          continue;
        }
      }

      if (!okResp) {
        throw lastErr || new Error("Falha ao solicitar saque");
      }

      toast.success("Solicitação de saque enviada! Aguarde aprovação.");
      setAmount("");

      if (typeof onSuccess === "function") onSuccess();
      if (typeof onClose === "function") onClose();
    } catch (error) {
      console.error("Erro ao solicitar saque:", error?.response?.data || error);
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.error ||
          "Erro ao solicitar saque"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="bg-slate-900 border-slate-800 text-white max-w-md"
        data-testid="withdrawal-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl">Solicitar Saque via PIX</DialogTitle>
          <DialogDescription className="text-slate-400">
            Preencha seus dados bancários para receber o saque.
          </DialogDescription>
        </DialogHeader>

        {loadingProfile ? (
          <div className="py-8 text-center text-slate-400">Carregando...</div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
              <div className="text-sm text-slate-400 mb-1">Saldo Disponível</div>
              <div className="text-2xl font-bold text-emerald-400">
                R$ {(Number(balance) || 0).toFixed(2)}
              </div>
              <div className="text-xs text-slate-500 mt-1">API: {API_BASE}</div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Valor do Saque
              </Label>
              <Input
                data-testid="withdrawal-amount-input"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-slate-800 border-slate-700 text-lg h-12"
              />
              <div className="grid grid-cols-3 gap-2 pt-1">
                {[50, 100, 200].map((v) => (
                  <Button
                    key={v}
                    size="sm"
                    variant="outline"
                    onClick={() => setAmount(v.toString())}
                    className="border-slate-700 hover:bg-slate-700"
                  >
                    R$ {v}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Chave PIX *
              </Label>
              <Input
                data-testid="pix-key-input"
                type="text"
                placeholder="CPF, Email, Telefone ou Chave Aleatória"
                value={pixKey}
                onChange={(e) => setPixKey(e.target.value)}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <User className="w-4 h-4" />
                CPF *
              </Label>
              <Input
                data-testid="cpf-input"
                type="text"
                placeholder="000.000.000-00"
                value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <Building className="w-4 h-4" />
                Banco (opcional)
              </Label>
              <Input
                data-testid="bank-name-input"
                type="text"
                placeholder="Nome do Banco"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mt-4">
              <div className="text-sm text-amber-300">
                ⚠️ O saque será processado em até 24 horas úteis após aprovação do administrador.
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 border-slate-700"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleWithdrawal}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                data-testid="submit-withdrawal-button"
              >
                {loading ? "Processando..." : "Solicitar Saque"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}